// MetaHead — met à jour <title>, <meta description>, og:title, og:description,
// og:locale et <html lang> en runtime, dans la langue DÉRIVÉE DE L'URL.
//
// Pourquoi la langue de l'URL et pas la langue UI courante ?
//   La langue UI est pilotée par la geoip (src/i18n/detectLang.js). Or
//   Googlebot/Bingbot crawlent majoritairement depuis des IP US → geoip = 'en'
//   → la page canonique `/` se faisait indexer en ANGLAIS sur le marché FR.
//   On découple donc :
//     - les META suivent l'URL (?lang=xx, sinon FR)  → pour le SEO / les crawlers
//     - l'UI suit toujours la geoip/navigator          → pour les humains (inchangé)
//   Sur `/` nu (sans ?lang=) → meta TOUJOURS FR, quelle que soit la géoloc.
//
// Périmètre :
//   - Web-only. Aucun impact sur l'UX langue de l'app native Capacitor, qui
//     reste pilotée par i18n (on ne touche ni detectLang.js ni config.js).
//   - Les crawlers Google/Bing modernes rendent le JS et voient ces meta runtime.
//   - Les hreflang sont statiques et cohérents dans public/index.html
//     (fr/en/es/it → ?lang=xx ; x-default → /).
//
// Sécurité :
//   - Aucun innerHTML. Manipulation via setAttribute uniquement.
//   - La langue est whitelistée (SUPPORTED_LANGS) avant tout usage.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '../i18n/config';

const LOCALE_MAP = { fr: 'fr_FR', en: 'en_GB', es: 'es_ES', it: 'it_IT' };
const SITE_URL = 'https://www.getrevyo.com';

// Langue des META = ?lang= de l'URL (normalisée + whitelistée), sinon FR.
// Indépendante de la langue UI runtime (geoip). Sur `/` nu → toujours FR.
function metaLangFromSearch(search) {
  try {
    const raw = new URLSearchParams(search || '').get('lang');
    if (raw) {
      const base = raw.toLowerCase().split('-')[0];
      if (SUPPORTED_LANGS.includes(base)) return base;
    }
  } catch (_) {}
  return DEFAULT_LANG;
}

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaByProperty(prop, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// Canonical auto-référent. Upsert d'un unique <link rel="canonical"> dans le head.
function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Usage :
 *   <MetaHead page="landing" />
 *   <MetaHead page="pricing" />
 *   <MetaHead page="howItWorks" />
 *   <MetaHead page="blog" />
 *   <MetaHead page="contact" />
 *
 * La clé `page` doit exister dans frontend/src/i18n/locales/<lang>/seo.json
 * avec au moins { title, description }. og_title et og_description sont
 * optionnels (fallback sur title/description).
 */
export default function MetaHead({ page }) {
  const { i18n } = useTranslation('seo');
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!page) return;

    // Langue des meta dérivée de l'URL (pas de la langue UI/geoip).
    const metaLang = metaLangFromSearch(search);
    // getFixedT lit le namespace 'seo' dans CETTE langue précise, quelle que
    // soit la langue UI active (toutes les langues sont bundlées au init).
    const t = i18n.getFixedT(metaLang, 'seo');

    const title = t(`${page}.title`);
    const description = t(`${page}.description`);
    const ogTitle = t(`${page}.og_title`, { defaultValue: title });
    const ogDesc = t(`${page}.og_description`, { defaultValue: description });

    if (title && typeof title === 'string') document.title = title;
    setMetaByName('description', description);
    setMetaByProperty('og:title', ogTitle);
    setMetaByProperty('og:description', ogDesc);
    setMetaByProperty('og:locale', LOCALE_MAP[metaLang] || 'fr_FR');

    // <html lang> = langue des META (page canonique), pas la langue UI/geoip.
    // Critique SEO : Google s'appuie dessus pour servir la bonne variante
    // hreflang. Sur `/` nu, on force donc FR même pour un crawler géolocalisé US.
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = metaLang;
    }

    // Canonical auto-référent, aligné sur les hreflang statiques de public/index.html
    // (FR → URL propre ; en/es/it → ?lang=xx). Corrige l'absence de canonical sur les
    // pages marketing (home, /tarifs, /comment-ca-marche…) constatée via GSC (2026-07-16).
    const canonicalUrl =
      SITE_URL + pathname + (metaLang !== DEFAULT_LANG ? `?lang=${metaLang}` : '');
    setCanonical(canonicalUrl);
  }, [page, pathname, search, i18n]);

  return null;
}
